(() => {
  'use strict';
  if (window.AlmondPorscheStory) { window.AlmondPorscheStory.mount(document); return; }
  const instances = new Map();
  const libraries = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
  const nativeOwners = new Set();
  const needsNativeScroll = () => !desktop.matches || navigator.maxTouchPoints > 0;
  // Clip the media itself so overlapping figures keep their layout and stacking.
  const revealTarget = el => {
    const media = el.querySelector('img,iframe');
    if (media) return media;
    let content = el.querySelector(':scope > .ps-wipe-content');
    if (!content) {
      content = document.createElement('div');
      content.className = 'ps-wipe-content';
      while (el.firstChild) content.appendChild(el.firstChild);
      el.appendChild(content);
    }
    return content;
  };
  const warmImage = el => {
    const image = el.querySelector('img');
    if (image) image.loading = 'eager';
  };
  let smoothOwner = null;

  // Version-pinned theme assets load once, only on pages containing this section.
  function loadLibrary(url, name) {
    if (window[name]) return Promise.resolve(window[name]);
    if (!url) return Promise.reject(new Error('Missing motion asset'));
    if (!libraries.has(url)) {
      libraries.set(url, new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => window[name] ? resolve(window[name]) : reject(new Error('Motion asset unavailable'));
        script.onerror = () => { libraries.delete(url); script.remove(); reject(new Error('Motion asset unavailable')); };
        document.head.appendChild(script);
      }));
    }
    return libraries.get(url);
  }

  function init(root) {
    if (instances.has(root)) return;
    let observer, context, lenis, generation = 0, destroyed = false;
    const entrances = new Map();
    const cleanups = [];
    const cleanup = () => {
      observer?.disconnect();
      observer = null;
      cleanups.splice(0).forEach(fn => fn());
      lenis?.destroy();
      lenis = null;
      if (smoothOwner === root) {
        smoothOwner = null;
        document.documentElement.classList.remove('ps-smooth-scroll');
      }
      context?.revert();
      context = null;
      entrances.clear();
      nativeOwners.delete(root);
      if (!nativeOwners.size) document.documentElement.classList.remove('ps-native-scroll');
      root.classList.remove('ps-gsap', 'ps-native-motion');
      root.querySelectorAll('.ps-pending,.ps-wipe-target').forEach(el => el.classList.remove('ps-pending', 'ps-wipe-target'));
    };
    function fallback() {
      if ('IntersectionObserver' in window) {
        root.classList.add('ps-native-motion');
        observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) {
            warmImage(entry.target);
            revealTarget(entry.target).classList.remove('ps-pending');
            observer.unobserve(entry.target);
          }
        }), {threshold: .06, rootMargin: '0px 0px 180px 0px'});
        root.querySelectorAll('[data-ps-reveal]').forEach(el => {
          if (el.getBoundingClientRect().top > window.innerHeight) {
            revealTarget(el).classList.add('ps-wipe-target', 'ps-pending');
            observer.observe(el);
          }
        });
      }
    }

    function enhanced(gsap, ScrollTrigger, ticket) {
      cleanup();
      gsap.registerPlugin(ScrollTrigger);
      root.classList.add('ps-gsap');
      context = gsap.context(() => {}, root);
      context.add(() => {
        const hero = root.querySelector('.ps-hero');
        if (hero && window.scrollY < 100) {
          gsap.fromTo(revealTarget(hero), {clipPath: 'inset(100% 0% 0% 0%)'}, {clipPath: 'inset(0% 0% 0% 0%)', duration: 1.8, ease: 'power2.inOut', clearProps: 'clipPath'});
        }
        root.querySelectorAll('[data-ps-reveal]').forEach(el => {
          // Never re-hide content already reached while libraries were loading.
          if (el.getBoundingClientRect().top < window.innerHeight * .94) return;
          const target = revealTarget(el);
          target.classList.add('ps-wipe-target');
          const tween = gsap.fromTo(target, {clipPath: 'inset(100% 0% 0% 0%)'}, {
            clipPath: 'inset(0% 0% 0% 0%)', duration: 1.8, ease: 'power2.inOut',
            clearProps: 'clipPath', scrollTrigger: {trigger: el, start: 'top 94%', once: true}
          });
          entrances.set(el, tween);
        });
      });
      // Clipped lazy images need an unclipped parent as their preload trigger.
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          warmImage(entry.target);
          observer.unobserve(entry.target);
        }), {rootMargin: '400px 0px'});
        root.querySelectorAll('[data-ps-reveal]').forEach(el => {
          if (el.querySelector('img')) observer.observe(el);
        });
      }
      const refresh = () => { if (!destroyed && ticket === generation) ScrollTrigger.refresh(); };
      root.querySelectorAll('img').forEach(img => {
        if (img.complete) return;
        img.addEventListener('load', refresh, {once: true});
        cleanups.push(() => img.removeEventListener('load', refresh));
      });
      document.fonts?.ready.then(refresh);
      refresh();

      if (needsNativeScroll() || root.dataset.smoothScroll !== 'true' || smoothOwner || document.documentElement.classList.contains('lenis')) return;
      loadLibrary(root.dataset.lenisUrl, 'Lenis').then(Lenis => {
        if (destroyed || ticket !== generation || needsNativeScroll() || reduced.matches || smoothOwner) return;
        document.documentElement.classList.add('ps-smooth-scroll');
        try {
          lenis = new Lenis({
            autoRaf: true, lerp: .085, smoothWheel: true, syncTouch: false, anchors: false,
            allowNestedScroll: true, stopInertiaOnNavigate: true,
            // Header, footer, dialogs and theme drawers retain their own scrolling.
            virtualScroll: ({event}) => root.contains(event.target) && !['hidden', 'clip'].includes(getComputedStyle(document.body).overflowY) && !['hidden', 'clip'].includes(getComputedStyle(document.documentElement).overflowY),
            prevent: node => !!node.closest?.('dialog,[role="dialog"],.m-drawer,[data-lenis-prevent]')
          });
          smoothOwner = root;
          lenis.on('scroll', ScrollTrigger.update);
          const nativeKey = event => {
            if (['Tab', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) lenis?.scrollTo(window.scrollY, {immediate: true});
          };
          window.addEventListener('keydown', nativeKey);
          cleanups.push(() => window.removeEventListener('keydown', nativeKey));
        } catch (_) {
          document.documentElement.classList.remove('ps-smooth-scroll');
          lenis?.destroy();
          lenis = null;
        }
      }).catch(() => {}); // Native scrolling remains fully usable if this asset fails.
    }

    const start = () => {
      const ticket = ++generation;
      cleanup();
      const native = needsNativeScroll();
      if (native) {
        nativeOwners.add(root);
        document.documentElement.classList.add('ps-native-scroll');
      }
      if (reduced.matches || root.dataset.motion !== 'true' || window.Shopify?.designMode) return;
      fallback();
      // Touch devices use CSS/IntersectionObserver reveals only. No Lenis or
      // ScrollTrigger refreshes can interrupt their native swipe momentum.
      if (native) return;
      loadLibrary(root.dataset.gsapUrl, 'gsap')
        .then(gsap => loadLibrary(root.dataset.scrolltriggerUrl, 'ScrollTrigger').then(ScrollTrigger => ({gsap, ScrollTrigger})))
        .then(({gsap, ScrollTrigger}) => {
          if (destroyed || ticket !== generation) return;
          try { enhanced(gsap, ScrollTrigger, ticket); }
          catch (_) { cleanup(); fallback(); }
        }).catch(() => {}); // Existing CSS/observer reveals are the offline fallback.
    };
    const revealFocused = event => {
      const el = event.target.closest('[data-ps-reveal]');
      if (el) revealTarget(el).classList.remove('ps-pending');
      entrances.get(el)?.progress(1);
      lenis?.scrollTo(window.scrollY, {immediate: true});
    };
    root.addEventListener('focusin', revealFocused);
    reduced.addEventListener('change', start);
    desktop.addEventListener('change', start);
    instances.set(root, () => {
      destroyed = true;
      generation++;
      cleanup();
      reduced.removeEventListener('change', start);
      desktop.removeEventListener('change', start);
      root.removeEventListener('focusin', revealFocused);
    });
    start();
  }
  const mount = scope => {
    if (scope.matches?.('[data-porsche-story]')) init(scope);
    scope.querySelectorAll('[data-porsche-story]').forEach(init);
  };
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, root) => {
      if (event.target === root || event.target.contains(root)) { destroy(); instances.delete(root); }
    });
  });
  document.addEventListener('shopify:block:select', event => {
    const el = event.target.closest('[data-ps-reveal]');
    if (el) revealTarget(el).classList.remove('ps-pending');
  });
  window.AlmondPorscheStory = {mount};
  mount(document);
})();
